import Analytics from '../analytics';
import HwpCollection from '../data/hwpCollection';
import Environment from '../environment';
import LinkHandler from '../linkHandler';
import { getLogger } from '../logging/logger';
import Android from '../platform/android';
import Comcast from '../platform/comcast';
import Verizon from '../platform/verizon';
import WebOS from '../platform/webos';
import { addReadyPromise } from '../util/utilities';

/**
 * @typedef {import('../platform/android').IntentOptions} IntentOptions
 *
 * @typedef {object} DeepLinkOptions
 * @property {string} comcast - Comcast platform specific deeplink
 * @property {string} verizon - Verizon platform specific deeplink
 * @property {string} webos - WebOS platform specific deeplink
 * @property {string} web - Web platform specific deeplink
 * @property {IntentOptions} android - Android platform specific deeplink
 *
 * @typedef {object} DeepLinkManagerOptions
 * @property {string} [contentUrl] - URL to fetch deeplink data from
 *
 * @typedef {Backbone.Model<DeepLinkOptions>} DeepLinkModel
 * @typedef {HwpCollection<DeepLinkModel>} DeepLinkCollection
 *
 */

const logger = getLogger('DeepLinkManager');
const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

/**
 * Manages platform-specific deep linking functionality across the application
 * @class
 * @extends {LinkHandler}
 */
class DeepLinkManager extends LinkHandler {
	/**
	 * @type {DeepLinkCollection}
	 * @description Collection of deeplinks that can be handled
	 */
	deeplinks = new HwpCollection();

	/**
	 * Regular expression pattern for matching deeplink protocol URIs
	 * @returns {RegExp} Pattern matching "deeplink://{deepLinkId}"
	 */
	get linkPattern() {
		return /^deeplink:\/\/(\w+)/;
	}

	/**
	 * Initializes deep link collection and sets up click listener
	 * @param {DeepLinkManagerOptions} options
	 */
	constructor(options = {}) {
		super();

		/** @type {Promise<DeepLinkManager>} */
		this.isReady = isReady;

		this.initialize(options);
	}

	/**
	 * @param {DeepLinkManagerOptions} options
	 */
	initialize(options) {
		const { contentUrl: url = '' } = options;

		if (!url) {
			// For simplicity and backwards compatibility, we force resolve successfully if there is no url
			onReadyResolve(this);
			this.trigger('initialized');
			return;
		}

		this.deeplinks.fetch({
			url,
			success: () => {
				onReadyResolve(this);
				this.trigger('initialized');
			},
			error: (error) => {
				logger.error(`[Error loading deeplink data]`, { error });
				onReadyReject(error);
			},
		});
	}

	/**
	 * Handles matched deeplink URIs by routing to platform-specific handlers
	 * @param {string} uri The deeplink URI to process
	 */
	handleLink(uri) {
		logger.log(`[${this.handleLink.name}]`, { uri });

		// Handle links that are pre-defined for specific platforms
		if (Comcast.linkPattern.test(uri)) {
			Comcast.handleLink(uri);
			return;
		} else if (Verizon.linkPattern.test(uri)) {
			Verizon.handleLink(uri);
			return;
		} else if (WebOS.linkPattern.test(uri)) {
			WebOS.handleLink(uri);
			return;
		}

		const [, id] = this.linkPattern.exec(uri) || [];
		const deepLink = this.deeplinks.get(id);
		logger.debug(`[Deeplink data]`, { ...deepLink?.toJSON() });

		if (!deepLink) {
			logger.warn(`No deeplink "${id}" not found`);
			Analytics.sendEvent('DeepLink-Error', { error: `Deeplink "${id}" not found` });
			return;
		}

		const { comcast, verizon, webos, android, web } = deepLink.attributes;
		if (Environment.isComcastSetTopBox() && comcast) {
			Analytics.sendEvent('DeepLink', { id: id, platform: 'comcast', url: comcast });
			Comcast.handleLink(comcast);
		} else if (Environment.isVerizonSetTopBox() && verizon) {
			Analytics.sendEvent('DeepLink', { id: id, platform: 'verizon', url: verizon });
			Verizon.handleLink(verizon);
		} else if (Environment.isWebOS() && webos) {
			Analytics.sendEvent('DeepLink', { id, platform: 'webos', url: webos });
			WebOS.handleLink(webos);
		} else if (Environment.isCordova() && android) {
			Analytics.sendEvent('DeepLink', { id, platform: 'android', url: android.url });
			Android.startActivity(android);
		} else if (web) {
			Analytics.sendEvent('DeepLink', { id: id, platform: 'web', url: web });
			window.location.href = web;
		} else {
			Analytics.sendEvent('DeepLink-Error', {
				error: `No platform handler found for "${id}"`,
			});
		}
	}
}

export default new DeepLinkManager();
